import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, Link2, Trash2, Sparkles } from "lucide-react";
import RiskMatrixPair from "./RiskMatrixPair";

const LEVEL_CLS = {
  critical: "border-red-500/40 bg-red-500/10 text-red-600",
  high: "border-orange-500/40 bg-orange-500/10 text-orange-600",
  medium: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  low: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
};

const LI_OPTIONS = [1, 2, 3, 4, 5];

const PLAN_STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", done: "Done" };
const PLAN_STATUS_CLS = {
  not_started: "border-muted-foreground/30 text-muted-foreground",
  in_progress: "border-sky-500/40 bg-sky-500/10 text-sky-600",
  done: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
};

function isOverdue(plan) {
  if (!plan || !plan.due_date || plan.status === "done") return false;
  return plan.due_date < new Date().toISOString().slice(0, 10);
}

function chipList(items) {
  if (!items || items.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((c, i) => (
        <span key={i} className="text-[10px] font-mono rounded border border-border bg-muted/40 px-1.5 py-0.5">{c}</span>
      ))}
    </div>
  );
}

function AddRiskModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Security");
  const [owner, setOwner] = useState("");
  const [likelihood, setLikelihood] = useState("3");
  const [impact, setImpact] = useState("3");
  const [threat, setThreat] = useState("");
  const [existingControls, setExistingControls] = useState("");
  const [busy, setBusy] = useState(false);

  const score = Number(likelihood) * Number(impact);

  const save = async () => {
    if (!title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/risks", {
        title, category, owner: owner || null,
        inherent_likelihood: Number(likelihood), inherent_impact: Number(impact),
        threat: threat || null,
        existing_controls: existingControls ? existingControls.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast({ title: "Risk added" });
      setTitle(""); setOwner(""); setThreat(""); setExistingControls("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add risk", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-risk-modal">
        <DialogHeader><DialogTitle>New risk</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" autoFocus data-testid="compliance-risk-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1" data-testid="compliance-risk-category-input" />
            </div>
            <div>
              <Label>Owner</Label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-risk-owner-input" />
            </div>
          </div>
          <div>
            <Label>Threat (what could go wrong)</Label>
            <Textarea value={threat} onChange={(e) => setThreat(e.target.value)} className="mt-1" rows={2} data-testid="compliance-risk-threat-input" />
          </div>
          <div>
            <Label>Existing controls (comma-separated codes)</Label>
            <Input value={existingControls} onChange={(e) => setExistingControls(e.target.value)} className="mt-1" placeholder="e.g. A.8.8, A.5.15" data-testid="compliance-risk-existing-controls-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Likelihood (1-5)</Label>
              <Select value={likelihood} onValueChange={setLikelihood}>
                <SelectTrigger className="mt-1" data-testid="compliance-risk-likelihood-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LI_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Impact (1-5)</Label>
              <Select value={impact} onValueChange={setImpact}>
                <SelectTrigger className="mt-1" data-testid="compliance-risk-impact-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LI_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-[12px] text-muted-foreground">Inherent score: <strong>{score}</strong> / 25</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-risk-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-risk-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add risk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RiskDetailDrawer({ risk, isAdmin, onClose, onChanged }) {
  const { toast } = useToast();
  const [plan, setPlan] = useState(risk.treatment_plan || null);
  const [editing, setEditing] = useState(!risk.treatment_plan);
  const [description, setDescription] = useState(risk.treatment_plan?.description || "");
  const [owner, setOwner] = useState(risk.treatment_plan?.owner || risk.owner || "");
  const [dueDate, setDueDate] = useState(risk.treatment_plan?.due_date || "");
  const [status, setStatus] = useState(risk.treatment_plan?.status || "not_started");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canEditResidual = isAdmin && risk.source !== "vendor_risk";
  const [resLikelihood, setResLikelihood] = useState(String(risk.residual_likelihood || 1));
  const [resImpact, setResImpact] = useState(String(risk.residual_impact || 1));
  const [appliedControls, setAppliedControls] = useState((risk.applied_controls || []).join(", "));
  const [savingResidual, setSavingResidual] = useState(false);
  const residualScore = Number(resLikelihood) * Number(resImpact);
  const [aiSuggesting, setAiSuggesting] = useState(false);

  const suggestTreatment = async () => {
    setAiSuggesting(true);
    try {
      const { data } = await api.post(`/compliance/risks/${risk.id}/ai-suggest-treatment`);
      setDescription(data.suggestion);
      setEditing(true);
      toast({ title: "AI suggestion added \u2014 review and save the plan below" });
    } catch (e) {
      toast({ title: "AI suggestion failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setAiSuggesting(false); }
  };

  const saveResidual = async () => {
    setSavingResidual(true);
    try {
      await api.put(`/compliance/risks/${risk.id}`, {
        residual_likelihood: Number(resLikelihood), residual_impact: Number(resImpact),
        applied_controls: appliedControls.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast({ title: "Residual assessment saved" });
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't save residual assessment", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSavingResidual(false); }
  };

  const save = async () => {
    if (!description.trim()) { toast({ title: "Description is required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/compliance/risks/${risk.id}/treatment-plan`, {
        description, owner: owner || null, due_date: dueDate || null, status,
      });
      setPlan(data);
      setEditing(false);
      toast({ title: "Treatment plan saved" });
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't save plan", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/compliance/risks/${risk.id}/treatment-plan`);
      setPlan(null);
      setDescription(""); setOwner(risk.owner || ""); setDueDate(""); setStatus("not_started");
      setEditing(true);
      toast({ title: "Treatment plan removed" });
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't remove plan", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="compliance-risk-detail-drawer">
        <DialogHeader>
          <DialogTitle>{risk.code} — {risk.title}</DialogTitle>
          <DialogDescription>Inherent {risk.inherent_level} ({risk.inherent_score}) · Residual {risk.residual_level} ({risk.residual_score})</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {risk.threat && (
            <p className="text-[12.5px] text-muted-foreground" data-testid="compliance-risk-detail-threat">{risk.threat}</p>
          )}
          <div className="grid grid-cols-2 gap-3 text-[11.5px]">
            <div>
              <Label className="text-[10.5px] uppercase text-muted-foreground">Existing controls</Label>
              <div className="mt-1">{chipList(risk.existing_controls)}</div>
            </div>
            <div>
              <Label className="text-[10.5px] uppercase text-muted-foreground">Applied controls</Label>
              <div className="mt-1">{chipList(risk.applied_controls)}</div>
            </div>
          </div>
          {canEditResidual && (
            <div className="rounded-lg border border-border p-3 space-y-2.5" data-testid="compliance-risk-residual-form">
              <div className="text-[12px] font-semibold">Residual assessment</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Residual likelihood</Label>
                  <Select value={resLikelihood} onValueChange={setResLikelihood}>
                    <SelectTrigger className="mt-1" data-testid="compliance-risk-residual-likelihood-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LI_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Residual impact</Label>
                  <Select value={resImpact} onValueChange={setResImpact}>
                    <SelectTrigger className="mt-1" data-testid="compliance-risk-residual-impact-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LI_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Applied controls (comma-separated codes)</Label>
                <Input value={appliedControls} onChange={(e) => setAppliedControls(e.target.value)} className="mt-1" data-testid="compliance-risk-applied-controls-input" />
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[12px] text-muted-foreground">Residual score: <strong>{residualScore}</strong> / 25</span>
                <Button size="sm" onClick={saveResidual} disabled={savingResidual} className="gap-1.5" data-testid="compliance-risk-residual-save-btn">
                  {savingResidual && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                </Button>
              </div>
            </div>
          )}
          {canEditResidual && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={suggestTreatment}
                disabled={aiSuggesting}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                data-testid="compliance-risk-ai-suggest-treatment-btn"
              >
                {aiSuggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {aiSuggesting ? "Thinking…" : "AI suggest treatment plan"}
              </button>
            </div>
          )}
          {!editing && plan ? (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="compliance-risk-plan-view">
              <div className="flex items-center justify-between gap-2">
                <span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", PLAN_STATUS_CLS[plan.status])}>{PLAN_STATUS_LABEL[plan.status]}</span>
                {isOverdue(plan) && <span className="text-[10px] font-semibold uppercase rounded-full border border-red-500/40 bg-red-500/10 text-red-600 px-2 py-0.5">Overdue</span>}
              </div>
              <p className="text-[12.5px] whitespace-pre-wrap">{plan.description}</p>
              <div className="flex items-center gap-3 text-[11.5px] text-muted-foreground">
                {plan.owner && <span>Owner: {plan.owner}</span>}
                {plan.due_date && <span>Due: {plan.due_date}</span>}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-3 pt-1">
                  <button type="button" onClick={() => setEditing(true)} className="text-[11.5px] font-medium text-primary hover:underline" data-testid="compliance-risk-plan-edit-btn">Edit plan</button>
                  <button type="button" onClick={remove} disabled={deleting} className="flex items-center gap-1 text-[11.5px] font-medium text-destructive hover:underline disabled:opacity-50" data-testid="compliance-risk-plan-delete-btn">
                    <Trash2 className="w-3 h-3" /> {deleting ? "Removing…" : "Remove plan"}
                  </button>
                </div>
              )}
            </div>
          ) : !editing ? (
            <div className="text-[12px] text-muted-foreground">No treatment plan yet.</div>
          ) : isAdmin ? (
            <div className="space-y-2.5" data-testid="compliance-risk-plan-form">
              <div>
                <Label>Remediation plan</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" rows={3} data-testid="compliance-risk-plan-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Owner</Label>
                  <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-risk-plan-owner-input" />
                </div>
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" data-testid="compliance-risk-plan-due-input" />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1" data-testid="compliance-risk-plan-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLAN_STATUS_LABEL).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                {plan && <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid="compliance-risk-plan-cancel-btn">Cancel</Button>}
                <Button size="sm" onClick={save} disabled={saving} className="gap-1.5" data-testid="compliance-risk-plan-save-btn">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {saving ? "Saving…" : "Save plan"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-[12px] text-muted-foreground">No treatment plan yet — only admins can add one.</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-risk-detail-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceRisks({ isAdmin }) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/compliance/risks");
      setRisks(data.risks || []);
    } catch { /* noop */ } finally { if (!silent) setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Keep the open drawer's risk prop fresh after a plan is saved/removed.
  useEffect(() => {
    if (!selected) return;
    const updated = risks.find((r) => r.id === selected.id);
    if (updated) setSelected(updated);
  }, [risks]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="compliance-risks-page">
      <RiskMatrixPair risks={risks} onSelect={setSelected} />
      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setModalOpen(true)} data-testid="compliance-risk-add-btn">
            <Plus className="w-3.5 h-3.5" /> New risk
          </Button>
        </div>
      )}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="compliance-risks-table">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Threat</TableHead>
              <TableHead>Existing controls</TableHead>
              <TableHead>Applied controls</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Inherent</TableHead>
              <TableHead>Residual</TableHead>
              <TableHead>Treatment plan</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {risks.map((r) => (
              <TableRow
                key={r.id}
                data-testid={`compliance-risk-row-${r.id}`}
                className={cn("cursor-pointer hover:bg-muted/40", r.source === "vendor_risk" && "bg-primary/[0.03]")}
                onClick={() => setSelected(r)}
              >
                <TableCell className="font-mono text-[12px]">{r.code}</TableCell>
                <TableCell className="text-[12.5px]">
                  {r.title}
                  {r.source === "vendor_risk" && (
                    <span className="flex items-center gap-1 mt-0.5 text-[10.5px] text-primary font-medium" data-testid={`compliance-risk-auto-badge-${r.id}`}>
                      <Link2 className="w-3 h-3" /> Auto-tracked from Vendor risk score ({r.vendor_risk_score})
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-[11.5px] text-muted-foreground max-w-[220px]">{r.threat || "—"}</TableCell>
                <TableCell className="max-w-[140px]">{chipList(r.existing_controls)}</TableCell>
                <TableCell className="max-w-[140px]">{chipList(r.applied_controls)}</TableCell>
                <TableCell className="text-[12px]">{r.owner || "—"}</TableCell>
                <TableCell><span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", LEVEL_CLS[r.inherent_level])}>{r.inherent_level} ({r.inherent_score})</span></TableCell>
                <TableCell><span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", LEVEL_CLS[r.residual_level])}>{r.residual_level} ({r.residual_score})</span></TableCell>
                <TableCell data-testid={`compliance-risk-plan-cell-${r.id}`}>
                  {r.treatment_plan ? (
                    <div className="flex items-center gap-1.5">
                      <span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", PLAN_STATUS_CLS[r.treatment_plan.status])}>{PLAN_STATUS_LABEL[r.treatment_plan.status]}</span>
                      {isOverdue(r.treatment_plan) && <span className="text-[10px] font-semibold uppercase rounded-full border border-red-500/40 bg-red-500/10 text-red-600 px-2 py-0.5">Overdue</span>}
                    </div>
                  ) : (
                    <span className="text-[11.5px] text-muted-foreground">No plan</span>
                  )}
                </TableCell>
                <TableCell className="text-[12px]">{r.status}</TableCell>
              </TableRow>
            ))}
            {risks.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center py-10" data-testid="compliance-risks-empty">
                <div className="text-[13px] font-medium">No risks logged yet</div>
                <div className="text-[12px] text-muted-foreground mt-1">Click "New risk" above to log your first risk, score its likelihood and impact, and track a treatment plan.</div>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <AddRiskModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
      {selected && (
        <RiskDetailDrawer risk={selected} isAdmin={isAdmin} onClose={() => setSelected(null)} onChanged={() => load({ silent: true })} />
      )}
    </div>
  );
}
